import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Cache in-memory ligero para consultas (Firestore u otras async).
 *
 * - TTL: dentro del TTL devuelve el valor cacheado sin volver a llamar.
 * - Stale-while-revalidate: al pasar el TTL pero antes de `staleMs`,
 *   devuelve el valor viejo inmediatamente y refresca en segundo plano.
 * - Deduplicacion: si N componentes piden la misma key simultaneamente,
 *   solo se ejecuta UNA llamada al fetcher; los demas esperan el mismo promise.
 *
 * No reemplaza una libreria como React Query, pero cubre el 80% de casos
 * sin agregar dependencias.
 */

type CacheEntry<T> = {
  value: T;
  fetchedAt: number;
  /** Datos historicos que no deben revalidarse (semanas pasadas). */
  immutable?: boolean;
};

type Pending<T> = {
  promise: Promise<T>;
  startedAt: number;
};

const cacheStore = new Map<string, CacheEntry<unknown>>();
const pendingStore = new Map<string, Pending<unknown>>();
const subscribers = new Map<string, Set<(value: unknown) => void>>();
/** Se incrementa en invalidate/force/seed para que fetches en vuelo no reescriban cache stale. */
const keyGeneration = new Map<string, number>();

const DEFAULT_TTL_MS = 30_000; // 30s: valor "fresco"
const DEFAULT_STALE_MS = 5 * 60_000; // 5min: aun aceptable mostrarlo mientras se refresca

export interface CachedFetchOptions {
  /** Tiempo en ms en que el valor se considera "fresco" y no se vuelve a pedir. Default: 30s. */
  ttlMs?: number;
  /**
   * Tiempo en ms en que un valor stale se sigue devolviendo mientras se refresca
   * en segundo plano. Default: 5min. Pasado este tiempo, se hace fetch bloqueante.
   */
  staleMs?: number;
  /** Forzar fetch ignorando el cache. */
  force?: boolean;
  /** Si true, nunca revalida una vez cacheado (ideal para semanas pasadas cerradas). */
  immutable?: boolean;
}

function notify<T>(key: string, value: T) {
  const subs = subscribers.get(key);
  if (!subs) return;
  subs.forEach(cb => cb(value));
}

function bumpGeneration(key: string): number {
  const next = (keyGeneration.get(key) ?? 0) + 1;
  keyGeneration.set(key, next);
  return next;
}

function currentGeneration(key: string): number {
  return keyGeneration.get(key) ?? 0;
}

export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {}
): Promise<T> {
  const ttl = options.ttlMs ?? DEFAULT_TTL_MS;
  const stale = options.staleMs ?? DEFAULT_STALE_MS;

  if (options.force) {
    return runFetch(key, fetcher, options);
  }

  const entry = cacheStore.get(key) as CacheEntry<T> | undefined;
  const now = Date.now();

  if (entry && (entry.immutable || options.immutable) && !options.force) {
    // Sella como immutable si el caller lo pide (semanas ya vistas no se vuelven a pedir).
    if (options.immutable && !entry.immutable) {
      cacheStore.set(key, { ...entry, immutable: true });
    }
    return entry.value;
  }

  if (entry) {
    const age = now - entry.fetchedAt;

    if (age < ttl) {
      return entry.value;
    }

    if (age < stale) {
      // Stale-while-revalidate: devolvemos el cache y refrescamos en background
      // (sin esperar). Si hay un fetch en curso, no duplicamos.
      if (!pendingStore.has(key)) {
        runFetch(key, fetcher, options).catch(() => { /* error ya logueado */ });
      }
      return entry.value;
    }
  }

  return runFetch(key, fetcher, options);
}

function runFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {}
): Promise<T> {
  if (options.force) {
    // No reutilizar un fetch en curso: puede ser pre-invalidate / pre-write.
    bumpGeneration(key);
    pendingStore.delete(key);
  } else {
    const inflight = pendingStore.get(key) as Pending<T> | undefined;
    if (inflight) {
      return inflight.promise;
    }
  }

  const generationAtStart = currentGeneration(key);

  const promise = (async () => {
    try {
      const value = await fetcher();
      if (currentGeneration(key) !== generationAtStart) {
        // Invalidado / forzado / seeded mientras esperábamos: no envenenar el cache.
        return value;
      }
      const existing = cacheStore.get(key) as CacheEntry<T> | undefined;
      cacheStore.set(key, {
        value,
        fetchedAt: Date.now(),
        immutable: options.immutable ?? existing?.immutable,
      });
      notify(key, value);
      return value;
    } finally {
      const pending = pendingStore.get(key) as Pending<T> | undefined;
      if (pending?.promise === promise) {
        pendingStore.delete(key);
      }
    }
  })();

  pendingStore.set(key, { promise, startedAt: Date.now() });
  return promise;
}

/** Invalida una key especifica. */
export function invalidateKey(key: string) {
  cacheStore.delete(key);
  pendingStore.delete(key);
  bumpGeneration(key);
}

/** Invalida todas las keys que empiecen con el prefijo. */
export function invalidatePrefix(prefix: string) {
  Array.from(cacheStore.keys()).forEach(k => {
    if (k.startsWith(prefix)) {
      cacheStore.delete(k);
      bumpGeneration(k);
    }
  });
  Array.from(pendingStore.keys()).forEach(k => {
    if (k.startsWith(prefix)) {
      pendingStore.delete(k);
      bumpGeneration(k);
    }
  });
}

/** Limpia TODO el cache. Usar al cerrar sesion. */
export function clearAllCache() {
  cacheStore.clear();
  pendingStore.clear();
  keyGeneration.clear();
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("agentSummary:")) {
        localStorage.removeItem(key);
      }
    });
  } catch {
  }
}

/** Escribe un valor en cache sin hacer fetch (prefetch en background). */
export function seedCache<T>(key: string, value: T, options?: { immutable?: boolean }) {
  bumpGeneration(key);
  cacheStore.set(key, {
    value,
    fetchedAt: Date.now(),
    immutable: options?.immutable,
  });
  notify(key, value);
}

/** Lee un valor del cache sin disparar fetch (devuelve undefined si no existe). */
export function peekCache<T>(key: string): T | undefined {
  const entry = cacheStore.get(key) as CacheEntry<T> | undefined;
  return entry?.value;
}

/** True si la entrada existe y está marcada immutable (no revalidar). */
export function isCacheImmutable(key: string): boolean {
  return cacheStore.get(key)?.immutable === true;
}

/** True si hay un fetch en curso para esa key (otros callers esperan la misma promesa). */
export function isFetchInFlight(key: string): boolean {
  return pendingStore.has(key);
}

/** Como peekCache, pero solo devuelve el valor si la entrada tiene menos de maxAgeMs. */
export function peekCacheIfFresh<T>(key: string, maxAgeMs: number): T | undefined {
  const entry = cacheStore.get(key) as CacheEntry<T> | undefined;
  if (!entry) return undefined;
  if (Date.now() - entry.fetchedAt >= maxAgeMs) return undefined;
  return entry.value;
}

/** Escucha actualizaciones de una key (fetch completado o seedCache). */
export function subscribeCache<T>(key: string, callback: (value: T) => void): () => void {
  const onUpdate = (value: unknown) => callback(value as T);
  let subs = subscribers.get(key);
  if (!subs) {
    subs = new Set();
    subscribers.set(key, subs);
  }
  subs.add(onUpdate);
  return () => {
    const set = subscribers.get(key);
    if (!set) return;
    set.delete(onUpdate);
    if (set.size === 0) subscribers.delete(key);
  };
}

export interface UseCachedQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

/**
 * Hook para consumir cachedFetch desde componentes.
 *
 * @example
 *   const { data: players, isLoading, refetch } = useCachedQuery(
 *     ['players', user.userName],
 *     () => showPlayers(user),
 *     [user.userName]
 *   );
 */
export function useCachedQuery<T>(
  key: string | (string | number | undefined)[],
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: CachedFetchOptions = {}
): UseCachedQueryResult<T> {
  const stableKey = Array.isArray(key) ? key.filter(Boolean).join(":") : key;
  const initial = peekCache<T>(stableKey);

  const [data, setData] = useState<T | undefined>(initial);
  const [isLoading, setIsLoading] = useState<boolean>(initial === undefined);
  const [error, setError] = useState<unknown>(null);
  const [trackedKey, setTrackedKey] = useState(stableKey);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Al cambiar de key, sincronizar desde cache en el mismo render (evita flash con data vieja).
  if (stableKey !== trackedKey) {
    setTrackedKey(stableKey);
    const cached = stableKey ? peekCache<T>(stableKey) : undefined;
    setData(cached);
    setIsLoading(Boolean(stableKey) && cached === undefined);
  }

  const run = useCallback(
    async (force = false) => {
      if (!stableKey) return;
      if (data === undefined) setIsLoading(true);
      try {
        const value = await cachedFetch<T>(stableKey, () => fetcherRef.current(), {
          ...optionsRef.current,
          force,
        });
        setData(value);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableKey, ...deps]
  );

  useEffect(() => {
    if (!stableKey) return;

    const cached = peekCache<T>(stableKey);
    const skipFetch =
      cached !== undefined &&
      (isCacheImmutable(stableKey) || optionsRef.current.immutable === true);

    setData(cached);
    setIsLoading(cached === undefined);

    const onUpdate = (value: unknown) => setData(value as T);

    let subs = subscribers.get(stableKey);
    if (!subs) {
      subs = new Set();
      subscribers.set(stableKey, subs);
    }
    subs.add(onUpdate);

    // Si ya está en cache immutable, no volver a pedir a Firestore.
    if (!skipFetch) {
      run();
    }

    return () => {
      const set = subscribers.get(stableKey);
      if (set) {
        set.delete(onUpdate);
        if (set.size === 0) subscribers.delete(stableKey);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey, ...deps]);

  const refetch = useCallback(() => run(true), [run]);

  return { data, isLoading, error, refetch };
}
